import { reverse } from 'named-urls'
import type { ComponentProps } from 'react'
import React from 'react'
import { StringParam, useQueryParam } from 'use-query-params'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { SECTION_NAMES } from '@constants/sectionNames'
import { useCreatorIntegrationsV2Query } from '@queries'
import { routing } from '@utils/routing'

import { type App, CATEGORIES, CATEGORY_LABELS, type Category } from '../Apps.types'
import type { AppsView } from '../AppsView'
import { thumbnails } from '../assets/thumbnails'
import { LearnMoreLink } from '../components/LearnMoreLink/LearnMoreLink'
import { useInstagramFeed } from '../pages/AppsInstagramFeed'
import { useMetaConversionPixel } from '../pages/AppsMetaConversions'
import { useTiktok } from '../pages/AppsTiktok'
import { usePlatformAppsQuery } from '../queries/usePlatformAppsQuery'
import { isAppConnected } from '../utils/isAppConnected'
import { mapStatusToTagProps } from '../utils/mapStatusToTagProps'

const HARDCODED_PLATFORM_APPS = [
  'apps.merchtrain',
  'apps.zapier',
  'apps.echobrain',
  'apps.vimmi',
  'apps.packiyo',
  'apps.mailchimp',
  'apps.convertkit',
  'apps.openai',
]

export const useApps = () => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { instagram } = useInstagramFeed()
  const { tiktok } = useTiktok() // TIKTOK_FEED
  const { pixelId } = useMetaConversionPixel()
  const { creatorIntegrationsV2Query } = useCreatorIntegrationsV2Query()
  const {
    platformAppsQuery: { data: platformApps },
  } = usePlatformAppsQuery()

  const [categoryParam, setCategoryParam] = useQueryParam('c', StringParam)

  const findApp = (
    app: NonNullable<typeof creatorIntegrationsV2Query.data>['apps'][number]['app'],
  ) => {
    return creatorIntegrationsV2Query.data?.apps.find((item) => item.app === app)
  }

  const platformAppState = (
    app: { state: 'INSTALLED' | 'NEEDS_RECONNECT' | 'UNINSTALLED' } | undefined,
  ) => {
    if (!app) return 'NOT_CONNECTED'

    if (app.state === 'INSTALLED') return 'CONNECTED'
    if (app.state === 'NEEDS_RECONNECT') return 'NOT_CONNECTED'
    if (app.state === 'UNINSTALLED') return 'NOT_CONNECTED'

    return 'NOT_CONNECTED'
  }

  const findPlatformApp = (app: string) => {
    return platformApps?.find((item) => item.id === app)
  }

  const coreApps: App[] = [
    {
      categories: ['social_commerce'],
      status: findApp('YOUTUBE_PRODUCT_SHELF')?.status,
      connected: isAppConnected(findApp('YOUTUBE_PRODUCT_SHELF')?.status),
      title: SECTION_NAMES.appYoutubeProductShelf,
      description: 'Promote featured products from your shop on your YouTube channels.',
      docs: routing.external.helpCenter.youtubeProductShelf,
      thumbnail: thumbnails.youtubeThumbnail,
      to: routing.apps.youTubeProductShelf,
      id: 'youtube-product-shelf',
    },
    {
      categories: ['social_commerce', 'memberships'],
      status: findApp('TWITCH_GIFTING')?.status,
      connected: isAppConnected(findApp('TWITCH_GIFTING')?.status),
      title: SECTION_NAMES.appTwitchGifting,
      description: "Allow your viewers to gift products and memberships for chat when you're live.",
      docs: routing.external.helpCenter.twitchGifting,
      thumbnail: thumbnails.twitchGiftingThumbnail,
      to: routing.apps.twitch,
      id: 'twitch-gifting',
    },
    {
      categories: ['social_commerce'],
      status: findApp('TWITTER_SHOP')?.status,
      connected: isAppConnected(findApp('TWITTER_SHOP')?.status),
      title: SECTION_NAMES.appTwitterShops,
      description: 'Show off your products on your X (Twitter) profile.',
      thumbnail: thumbnails.twitterShopsThumbnail,
      to: routing.apps.twitterShops,
      id: 'twitter-shops',
    },
    {
      categories: ['social_feeds'],
      status: tiktok.status,
      connected: isAppConnected(tiktok.status),
      title: SECTION_NAMES.appTikTok,
      description:
        'Highlight TikTok videos on your homepage and share thank you notes to your TikTok account.',
      thumbnail: thumbnails.tiktokThumbnail,
      to: routing.apps.tiktok,
      id: 'tiktok',
    },
    {
      categories: ['social_commerce'],
      status: findApp('TIKTOK_SHOP')?.status,
      connected: isAppConnected(findApp('TIKTOK_SHOP')?.status),
      title: SECTION_NAMES.appTikTokShop,
      description: 'Showcase and sell products from your shop directly to your fans on TikTok.',
      docs: routing.external.helpCenter.tiktokShop,
      thumbnail: thumbnails.tiktokShopThumbnail,
      to: routing.apps.tiktokShop,
      id: 'tiktok-shop',
    },
    {
      categories: ['social_commerce'],
      status: pixelId ? 'CONNECTED' : 'NOT_CONNECTED',
      connected: !!pixelId,
      title: SECTION_NAMES.appMetaCAPI,
      description: 'Send conversion events to Meta for running paid ad campaigns for your shop.',
      docs: routing.external.helpCenter.metaCAPI,
      thumbnail: thumbnails.metaCAPIThumbnail,
      to: routing.apps.metaCAPI,
      id: 'meta-capi',
    },
    {
      categories: ['social_feeds'],
      status: instagram.status,
      connected: isAppConnected(instagram.status),
      title: SECTION_NAMES.appInstagram,
      description: 'Showcase your Instagram feed on your homepage.',
      thumbnail: thumbnails.instagramThumbnail,
      to: routing.apps.instagramFeed,
      id: 'instagram-feed',
    },
    {
      categories: ['social_commerce'],
      status: findApp('INSTAGRAM_SHOP')?.status,
      connected: isAppConnected(findApp('INSTAGRAM_SHOP')?.status),
      title: SECTION_NAMES.appMeta,
      description:
        'Showcase and sell products from your shop directly to your fans on Instagram and Facebook.',
      docs: routing.external.helpCenter.meta,
      thumbnail: thumbnails.metaThumbnail,
      to: routing.apps.meta,
      hidden: !dashboardFeatureFlags.appMeta.enabled,
      id: 'meta',
    },

    {
      categories: ['social_commerce'],
      status: platformAppState(findPlatformApp('apps.merchtrain')),
      connected: isAppConnected(platformAppState(findPlatformApp('apps.merchtrain'))),
      title: 'Twitch Merch Train',
      description:
        'A hype train, but for merch. Give out rewards for rapid sequences of viewer purchases on your Twitch stream.',
      thumbnail: thumbnails.merchtrainThumbnail,
      docs: routing.external.helpCenter.merchtrain,
      to: routing.apps.merchtrain,
      id: 'merchtrain',
      type: 'beta',
    },
    {
      categories: ['other'],
      status: platformAppState(findPlatformApp('apps.zapier')),
      connected: isAppConnected(platformAppState(findPlatformApp('apps.zapier'))),
      title: 'Zapier',
      description: 'Automate your workflows by connecting to thousands of apps via Zapier.',
      thumbnail: thumbnails.zapierThumbnail,
      to: routing.apps.zapier,
      id: 'zapier',
    },
    {
      categories: ['alerts'],
      status: findApp('STREAMELEMENTS')?.status,
      connected: isAppConnected(findApp('STREAMELEMENTS')?.status),
      title: SECTION_NAMES.appStreamElements,
      description: 'Show stream alerts for product purchases and gifts via StreamElements.',
      docs: routing.external.helpCenter.streamAlerts,
      thumbnail: thumbnails.streamelementsThumbnail,
      to: routing.apps.streamelements,
      id: 'streamelements',
    },
    {
      categories: ['alerts'],
      status: findApp('STREAMLABS')?.status,
      connected: isAppConnected(findApp('STREAMLABS')?.status),
      title: SECTION_NAMES.appStreamlabs,
      description: 'Show stream alerts for product purchases and gifts via Streamlabs.',
      docs: routing.external.helpCenter.streamAlerts,
      thumbnail: thumbnails.streamlabsThumbnail,
      to: routing.apps.streamlabs,
      id: 'streamlabs',
    },
    {
      categories: ['memberships'],
      status: 'NOT_CONNECTED',
      connected: false,
      title: SECTION_NAMES.appSpotify,
      description: 'Allow members to listen to your audio posts on Spotify.',
      docs: routing.external.helpCenter.streamAlerts,
      thumbnail: thumbnails.spotifyThumbnail,
      to: routing.apps.spotify.self,
      id: 'spotify',
      hidden: !dashboardFeatureFlags.appSpotify.enabled,
    },
    {
      categories: ['memberships'],
      status: findApp('FWDISCORD')?.status,
      connected: isAppConnected(findApp('FWDISCORD')?.status),
      title: 'Members-only Discord server',
      description: 'Allows you to offer access to your Discord server with your members.',
      thumbnail: thumbnails.discordThumbnail,
      to: routing.apps.discord,
      hidden: !dashboardFeatureFlags.memberships.enabled,
      id: 'discord',
    },
    {
      categories: ['social_commerce', 'memberships'],
      status: findApp('TWITCH_DISCOUNTS_FOR_SUBS')?.status,
      connected: isAppConnected(findApp('TWITCH_DISCOUNTS_FOR_SUBS')?.status),
      title: 'Discounts for Twitch subs',
      description: 'Offer Twitch subscribers discounts on membership tiers.',
      docs: routing.external.helpCenter.twitchMembershipsIntegrations,
      thumbnail: thumbnails.twitchMembershipsThumbnail,
      to: routing.apps.twitchMembershipsDiscounts,
      id: 'twitch-memberships-discounts',
    },
    {
      categories: ['email_marketing'],
      status: findApp('KLAVIYO')?.status,
      connected: isAppConnected(findApp('KLAVIYO')?.status),
      title: 'Klaviyo',
      description: 'Use your Klaviyo account to sync support emails and send custom emails.',
      docs: routing.external.helpCenter.klaviyo,
      thumbnail: thumbnails.klaviyoThumbnail,
      to: routing.apps.klaviyo,
      id: 'klaviyo',
    },
    {
      categories: ['email_marketing'],
      status: findApp('MAILCHIMP')?.status,
      connected: isAppConnected(findApp('MAILCHIMP')?.status),
      title: 'Mailchimp',
      description: 'Use Mailchimp to sync supporter emails and send custom emails.',
      thumbnail: thumbnails.mailchimpThumbnail,
      to: routing.apps.mailchimp,
      id: 'mailchimp',
    },
    {
      categories: ['email_marketing'],
      status: findApp('KIT')?.status,
      connected: isAppConnected(findApp('KIT')?.status),
      title: 'Kit',
      description: 'Use Kit to sync purchases and email lists.',
      thumbnail: thumbnails.kitThumbnail,
      to: routing.apps.kit.self,
      id: 'kit',
    },
    {
      categories: ['email_marketing'],
      status: findApp('BEEHIIV')?.status,
      connected: isAppConnected(findApp('BEEHIIV')?.status),
      title: 'beehiiv',
      description: 'Use your beehiiv account to sync supporter emails and newsletter signups.',
      docs: routing.external.helpCenter.beehiiv,
      thumbnail: thumbnails.beehiivThumbnail,
      to: routing.apps.beehiiv,
      id: 'beehiiv',
    },
    {
      categories: ['email_marketing'],
      status: findApp('LAYLO')?.status,
      connected: isAppConnected(findApp('LAYLO')?.status),
      title: 'Laylo',
      description: 'Use Laylo to announce and promote your drops directly to your supporters.',
      docs: routing.external.helpCenter.laylo,
      thumbnail: thumbnails.layloThumbnail,
      to: routing.apps.laylo,
      id: 'laylo',
    },
    {
      categories: ['other'],
      status: findApp('BOOKFUNNEL')?.status,
      connected: isAppConnected(findApp('BOOKFUNNEL')?.status),
      title: 'BookFunnel',
      description: 'Use BookFunnel to deliver e-books for orders sold on Fourthwall.',
      thumbnail: thumbnails.bookfunnelThumbnail,
      to: routing.apps.bookfunnel,
      id: 'bookfunnel',
      type: 'thirdParty',
      hidden: !dashboardFeatureFlags.appBookfunnel.enabled,
    },
    {
      categories: ['other'],
      status: findApp('LINKTREE')?.status,
      connected: isAppConnected(findApp('LINKTREE')?.status),
      title: 'Linktree',
      description: 'Add your shop as a link in your Linktree bio.',
      thumbnail: thumbnails.linktreeThumbnail,
      to: routing.apps.linktree,
      id: 'linktree',
      hidden: !dashboardFeatureFlags.appLinktree.enabled,
    },
    {
      categories: ['other'],
      status: findApp('PLEDGE')?.status,
      connected: isAppConnected(findApp('PLEDGE')?.status),
      title: 'Donations by Pledge',
      description: 'Send a portion of your donation proceeds to a non-profit via Pledge.',
      docs: routing.external.helpCenter.twitchMembershipsIntegrations,
      thumbnail: thumbnails.pledgeThumbnail,
      to: routing.apps.pledge,
      hidden: !dashboardFeatureFlags.appPledge.enabled,
      id: 'pledge',
    },
    {
      categories: ['other'],
      status: findApp('SHIPSTATION')?.status,
      connected: isAppConnected(findApp('SHIPSTATION')?.status),
      title: 'ShipStation',
      description: 'Manage the shipping experience of products you are responsible for fulfilling.',
      docs: routing.external.helpCenter.shipStationIntegration,
      thumbnail: thumbnails.shipstationThumbnail,
      id: 'shipstation',
      to: routing.apps.shipStation,
    },
    {
      categories: ['other'],
      status: platformAppState(findPlatformApp('apps.echobrain')),
      connected: isAppConnected(platformAppState(findPlatformApp('apps.echobrain'))),
      title: 'Customer Reviews by EchoBrain',
      description: 'Display customer reviews on your product pages with EchoBrain.',
      docs: routing.external.echobrain.learnMore,
      thumbnail: thumbnails.echobrainThumbnail,
      id: 'echobrain',
      to: routing.apps.echobrain,
      type: 'thirdParty',
    },
    {
      categories: ['other'],
      status: platformAppState(findPlatformApp('apps.packiyo')),
      connected: isAppConnected(platformAppState(findPlatformApp('apps.packiyo'))),
      title: 'Packiyo',
      description: 'Use Packiyo to sync products.',
      thumbnail: thumbnails.packiyoThumbnail,
      to: routing.apps.packiyo,
      hidden: !dashboardFeatureFlags.appPackiyo.enabled,
      id: 'packiyo',
      type: 'beta',
    },
    {
      categories: ['other'],
      status: platformAppState(findPlatformApp('apps.vimmi')),
      connected: isAppConnected(platformAppState(findPlatformApp('apps.vimmi'))),
      title: 'Vimmi Video Commerce',
      description: 'Live shopping and shoppable video platform for your store and socials.',
      docs: routing.external.vimmi.learnMore,
      thumbnail: thumbnails.vimmiThumbnail,
      to: routing.apps.vimmi,
      id: 'vimmi',
      type: 'thirdParty',
    },

    // NOTE: Use following thumbnail in case it's missing for a new app.
    {
      categories: ['social_commerce'],
      status: 'CONNECTED',
      connected: true,
      title: 'App fallback',
      description: 'Very cool and short description for an app goes here.',
      docs: 'https://help.fourthwall.com/',
      thumbnail: thumbnails.fallbackThumbnail,
      to: routing.apps.self,
      hidden: true,
      id: 'app-fallback',
    },
  ]

  function getAppType(appStatus: 'DRAFT' | 'COMMUNITY' | 'SUBMITTED' | 'PUBLISHED') {
    if (appStatus === 'DRAFT') return 'draft'
    if (appStatus === 'COMMUNITY') return 'community'

    return 'thirdParty'
  }

  const additionalApps: App[] = (
    platformApps?.filter((app) => !HARDCODED_PLATFORM_APPS.includes(app.id)) || []
  ).map((app) => ({
    categories: ['other'],
    status: platformAppState(app),
    connected: isAppConnected(platformAppState(app)),
    title: app.displayName,
    description: app.description,
    thumbnail: app.iconUrl || '',
    to: reverse(routing.apps.app, { app: app.id }),
    id: app.id,
    type: getAppType(app.status),
  }))

  const enabledApps = [
    ...[...coreApps, ...additionalApps].filter((app) => app.status === 'CONNECTED' && !app.hidden),
    ...coreApps.filter((app) => app.status !== 'CONNECTED' && !app.hidden),
    ...additionalApps.filter((app) => app.status !== 'CONNECTED' && app.type !== 'draft'),
    ...additionalApps.filter((app) => app.status !== 'CONNECTED' && app.type === 'draft'),
  ]

  const displayedApps = enabledApps.filter((app) => {
    return categoryParam ? app.categories.includes(categoryParam as Category) : true
  })

  const displayedCategories = CATEGORIES.filter((category) => {
    return enabledApps.some((app) => app.categories.includes(category))
  })

  const tabs: ComponentProps<typeof AppsView>['tabs'] = {
    options: [
      { label: 'All', value: '' },
      ...displayedCategories.map((category) => ({
        label: CATEGORY_LABELS[category],
        value: category,
      })),
    ],
    value: categoryParam || '',
    onChange: (value) => setCategoryParam(value),
  }

  const apps: ComponentProps<typeof AppsView>['apps'] = displayedApps.map((app) => ({
    ...app,
    description: (
      <>
        {app.description} {app.docs && <LearnMoreLink href={app.docs} />}
      </>
    ),
    tag: mapStatusToTagProps(app.status, false),
    type: app.type,
  }))

  return {
    tabs,
    apps,
    isAnyAppConnected: enabledApps.some((item) => item.connected),
  }
}
